import { useShoppingCart } from '@/contexts';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { useEffect } from 'react';
import {
	useCreateSessionForOneOffPaymentMutation,
	useFetchStripePublishableKeyQuery,
} from './graphql.generated';

// We only need to load this once.
let _stripePromise: Promise<Stripe | null> | undefined | null = undefined;

export const useStripePromise = () => {
	const { totalItemsInCart, quantityPerPriceItemId } = useShoppingCart();
	const {
		data: pubkData,
		loading: pubkLoading,
		error: pubkError,
	} = useFetchStripePublishableKeyQuery({
		fetchPolicy: 'cache-first',
	});
	const [fetchSessionKey, { data: sessionData, loading: sessionLoading, error: sessionError }] =
		useCreateSessionForOneOffPaymentMutation();

	/** Fetch the publishable key. It lives on the server for easy update/revoke */
	useEffect(() => {
		if (pubkData?.stripePublishableKey) {
			_stripePromise = loadStripe(pubkData.stripePublishableKey);
		}
	}, [pubkData?.stripePublishableKey]);

	/** Create a Stripe Session, which returns a secret session key for user to complete the transaction */
	useEffect(() => {
		if (totalItemsInCart > 0) {
			fetchSessionKey({
				variables: {
					lineItems: Array.from(quantityPerPriceItemId).map(([priceId, quantity]) => ({
						priceId,
						quantity,
					})),
				},
			});
		}
	}, [totalItemsInCart, quantityPerPriceItemId, fetchSessionKey]);

	if (pubkError || sessionError) {
		console.error(pubkError || sessionError);
	}

	return {
		stripePromise: _stripePromise,
		clientSecret: sessionData?.createSessionForOneOffPayment?.clientSecret,
		loading: pubkLoading || sessionLoading,
		error: pubkError || sessionError,
	};
};
